import classNames from 'classnames'
import credentials from 'common/ProviderCredentials/credentials'
import { DEFAULT_PROFILE_IMAGE_PATH } from 'common/ProviderPreview/constants'
import Spinner from 'common/Spinner'
import { Button as CLButton } from 'component_library/1_Atoms'
import { getHighlightingInfo } from 'components/app/ProviderSearch/utils'
import { dateFormats, sessionStorageKeys, TimeOfDay } from 'constants/common'
import { InsuranceProviderContext } from 'context/common/InsuranceProvider'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import { getProviderProfileRoute } from 'dataSource/ProviderSearch/routes'
import useProviderListingDetails from 'hooks/provider/useProviderListingDetails-v3'
import useAppSelector from 'hooks/useAppSelector'
import useThriveAuth from 'hooks/useThriveAuth'
import _compact from 'lodash/compact'
import _flatten from 'lodash/flatten'
import _isEmpty from 'lodash/isEmpty'
import Image from 'next/legacy/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useContext, useEffect, useMemo, useState } from 'react'
import _upperFirst from 'lodash/upperFirst'
import {
  getParsedQuery,
  getPracticeAddressFeatures,
  getStringifiedQuery,
} from 'utils/common'
import { formatProviderPronouns } from 'utils/formatProviderPronouns'
import BonusBadge from './BonusBadge'
import styles from './index.module.scss'
import {
  getSearchMatchSlotLocation,
  PROVIDER_PROFILE_SLOT_LOCATION_QUERY,
} from 'app/ProviderSearch/constants'
import {
  renderInsuranceSection,
  renderSpecialtySection,
} from './renderHighlightedText'
import { isNextOpeningSlotToday } from './utils'

type MobileListCardProviderSectionProps = {
  providerDetails: Provider
  areSlotsLoading: boolean
  generatedSlots: DateSlot
  nextAvailabilitySlot: ProviderSlot
  practice: Practice
  onNextOpeningClick: (
    selectedSlot: SelectedSlot,
    logNextOpeningRudderEvent: boolean
  ) => void
  showModal?: () => void
  renderCustomInsuranceSection?: () => JSX.Element
  matchedBonusTimeOfDay?: TimeOfDay[]
  viewAllAvailabilityClassName?: string
  slotLocation?: string
}

const MobileListCardProviderSection: React.FC<MobileListCardProviderSectionProps> = ({
  providerDetails,
  practice,
  areSlotsLoading,
  generatedSlots,
  nextAvailabilitySlot,
  onNextOpeningClick,
  showModal,
  renderCustomInsuranceSection,
  matchedBonusTimeOfDay,
  viewAllAvailabilityClassName,
  slotLocation,
}) => {
  const { globalGroupedFilters } = useAppSelector((state) => state.global)
  const { isSupportOrThirdPartyUser } = useThriveAuth()
  const { insuranceProvider } = useContext(InsuranceProviderContext)

  const router = useRouter()
  const { query } = router
  const [isAvailabilityInPerson, setIsAvailabilityInPerson] = useState<boolean>(
    false
  )

  // Get highlighting information from URL query parameters (for existing specialty highlighting)
  const { selectedSpecialtyIds } = getHighlightingInfo(router)

  // Get area of concern filters for specialty highlighting
  const areaOfConcernFilters =
    globalGroupedFilters[ProviderFilterTypes.AREA_OF_CONCERN] || []

  // Convert selected specialty IDs to names for highlighting
  const selectedSpecialtyNames = selectedSpecialtyIds
    .map((id) => areaOfConcernFilters.find((filter) => filter.id === id)?.value)
    .filter(Boolean)

  const { firstName, lastName, pronouns, profileImagePath, title } =
    providerDetails ?? {}
  const {
    areaOfConcernValues,
    insuranceNamesValues,
    allAreasOfConcernValues,
  } = useProviderListingDetails(providerDetails, isSupportOrThirdPartyUser)
  const {
    city,
    state,
    streetAddress,
    aptOrSuite,
    zipcode,
  } = getPracticeAddressFeatures(practice)
  const availabilityTypeFilters =
    globalGroupedFilters[ProviderFilterTypes.AVAILABILITY_TYPE]

  const providerCredentials = credentials[title] || {}

  const nextAvailabilitySlotObj = useMemo(() => {
    const firstAvailableDateSlot = _flatten(Object.values(generatedSlots)).find(
      (date) => !!date
    )

    return firstAvailableDateSlot || nextAvailabilitySlot
  }, [generatedSlots])

  const { format } = useContext(TimeZoneContext)
  const providerProfileLink = getProviderProfileRoute(providerDetails)
  const formattedQuery = getParsedQuery(router)
  const searchMatchSlotLocation = getSearchMatchSlotLocation(slotLocation)
  const queryObject = {
    ...(query?.insuranceId && { insuranceId: query.insuranceId }),
    ...(practice?.distanceInMiles && {
      distanceInMiles: practice.distanceInMiles.toFixed(2),
    }),
    ...(!_isEmpty(formattedQuery?.counselingType) && {
      counselingType: formattedQuery.counselingType,
    }),
    ...(!_isEmpty(formattedQuery?.availabilityType) && {
      availabilityType: formattedQuery.availabilityType[0],
    }),
    ...(!_isEmpty(formattedQuery?.areaOfConcern) && {
      areaOfConcern: formattedQuery.areaOfConcern,
    }),
    ...(searchMatchSlotLocation && {
      [PROVIDER_PROFILE_SLOT_LOCATION_QUERY]: searchMatchSlotLocation,
    }),
  }
  const serializedQueryObject = getStringifiedQuery(queryObject)

  useEffect(() => {
    availabilityTypeFilters.map((avail) => {
      if (
        avail.id === queryObject?.availabilityType &&
        avail.value === 'In Person'
      ) {
        setIsAvailabilityInPerson(true)
      }
    })
  }, [queryObject])

  const formattedPronouns = formatProviderPronouns(pronouns)

  // Store selected specialty IDs in session storage before navigation
  const handleProviderCardClick = () => {
    if (selectedSpecialtyIds.length > 0) {
      sessionStorage.setItem(
        sessionStorageKeys.SELECTED_SPECIALTY_IDS,
        JSON.stringify(selectedSpecialtyIds)
      )
    }
  }

  return (
    <div className={styles.ProviderCard}>
      <Link
        href={{
          pathname: providerProfileLink,
          query: serializedQueryObject,
        }}
        passHref
        legacyBehavior
      >
        <a
          href=" "
          className="text-decoration-none"
          onClick={handleProviderCardClick}
        >
          <div className={styles.ProviderCard_container}>
            <div className={styles.ProviderCard_container_header}>
              <div
                className={styles.ProviderCard_container_header_providerImage}
              >
                <Image
                  unoptimized={!!profileImagePath}
                  src={profileImagePath || DEFAULT_PROFILE_IMAGE_PATH}
                  alt={`${firstName} ${lastName}'s profile image`}
                  layout="fill"
                  className="rounded-circle c-pointer"
                  objectFit="cover"
                  objectPosition="top"
                />
              </div>

              <div
                className={styles.ProviderCard_container_header_providerInfo}
              >
                <div
                  className={
                    styles.ProviderCard_container_header_providerInfo_container
                  }
                >
                  <h3>
                    <span
                      className={
                        styles.ProviderCard_container_header_providerInfo_container_name
                      }
                    >
                      {`${firstName} ${lastName}`}{' '}
                    </span>
                    {/* Display pronouns with slash between sets (e.g. She/Her/They/Them) */}
                    {formattedPronouns && (
                      <span
                        className={
                          styles.ProviderCard_container_header_providerInfo_container_pronouns
                        }
                      >
                        {formattedPronouns}
                      </span>
                    )}
                  </h3>
                </div>

                <p className={styles.credentials}>
                  {providerCredentials.definition && (
                    <span>
                      {`${providerCredentials.definition}
                          ${
                            title !== providerCredentials.definition
                              ? ` (${title})`
                              : ''
                          }
                        `}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className={styles.InfoContainer}>
              {renderCustomInsuranceSection
                ? renderCustomInsuranceSection()
                : insuranceNamesValues.length > 0 && (
                    <div className={styles.InfoContainer_insurance}>
                      <div className={styles.InfoContainer_insurance_icon}>
                        <img
                          src="/icons/shield-check.svg"
                          srcSet="/icons/shield-check1.webp"
                          alt="insurance"
                        />
                      </div>
                      <div className={styles.InfoContainer_insurance_list}>
                        {renderInsuranceSection(
                          insuranceProvider?.name,
                          providerDetails.insuranceNames,
                          insuranceNamesValues
                        )}
                      </div>
                    </div>
                  )}
              {areaOfConcernValues.length > 0 && (
                <div className={styles.InfoContainer_speciality}>
                  <div className={styles.InfoContainer_speciality_icon}>
                    <img
                      src="/icons/armchair.svg"
                      srcSet="/icons/armchair.webp"
                      alt="speciality"
                    />
                  </div>
                  <div className={styles.InfoContainer_speciality_list}>
                    {renderSpecialtySection(
                      selectedSpecialtyNames,
                      allAreasOfConcernValues,
                      areaOfConcernValues,
                      providerDetails.areaOfConcernIds
                    )}
                  </div>
                </div>
              )}
              {isAvailabilityInPerson && !isSupportOrThirdPartyUser ? (
                <div className={styles.InfoContainer_availability}>
                  <div className={styles.InfoContainer_availability_icon}>
                    <img
                      src="/icons/map-pin.svg"
                      srcSet="/icons/map-pin.webp"
                      alt="address"
                    />
                  </div>
                  <div className={styles.InfoContainer_availability_list}>
                    {city}, {state} |{' '}
                    {`${practice.distanceInMiles.toFixed(2)}mi`}
                  </div>
                </div>
              ) : null}
              {isSupportOrThirdPartyUser && (
                <div className={styles.InfoContainer_availability}>
                  <div className={styles.InfoContainer_availability_icon}>
                    <img
                      src="/icons/map-pin.svg"
                      srcSet="/icons/map-pin.webp"
                      alt="address"
                    />
                  </div>

                  <div className={styles.InfoContainer_availability_list}>
                    <div>
                      {_compact([streetAddress, aptOrSuite]).join(', ')}
                    </div>
                    <div>
                      {city}, {state} {zipcode} |{' '}
                      {`${practice.distanceInMiles.toFixed(2)}mi`}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {matchedBonusTimeOfDay && matchedBonusTimeOfDay.length > 0 && (
              <BonusBadge matchedTimeOfDay={matchedBonusTimeOfDay} />
            )}
          </div>
        </a>
      </Link>
      <div className={styles.next_opening_container}>
        {/* always display the button when slots are loading to display loader inside the button
          if have nextAvailableDate then display the date inside the button else dont display the button */}
        {(nextAvailabilitySlotObj || areSlotsLoading) && (
          <CLButton
            theme="v2"
            variant="primary"
            full
            aria-label="Select next available opening slot"
            onClick={() =>
              onNextOpeningClick(
                {
                  slot: nextAvailabilitySlotObj,
                  provider: providerDetails,
                },
                true
              )
            }
            disabled={areSlotsLoading}
            className={styles.next_opening_btn}
          >
            {areSlotsLoading ? (
              <Spinner />
            ) : isNextOpeningSlotToday(nextAvailabilitySlotObj, format) ? (
              `Next opening: Today, ${format(
                nextAvailabilitySlotObj?.start,
                ` ${dateFormats.TIME_FORMAT_LOWER}`
              ).replace(':00', '')}`
            ) : (
              `Next opening: ${format(
                nextAvailabilitySlotObj?.start,
                `${dateFormats.DAY_FORMAT} ${dateFormats.MONTH_DATE_SINGLEDIGIT_FORMAT}, ${dateFormats.TIME_FORMAT_LOWER}`
              ).replace(':00', '')}`
            )}
          </CLButton>
        )}
      </div>
      <button
        type="button"
        onClick={showModal}
        className={`${classNames(
          styles.view_all_availability_button,
          viewAllAvailabilityClassName
        )} ~bg-transparent ~border-0 ~px-3 ~py-[0.375rem]`}
      >
        View all availability
      </button>
    </div>
  )
}

export default MobileListCardProviderSection
